import React, { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Upload, ImageIcon } from "lucide-react";

import "./HostEvent.css";

const EMPTY_HOST_FORM = {
  name: "",
  email: "",
  phone: "",
  company: "",
  role: "",
  website: "",
  proposed_title: "",
  proposed_date: "",
  expected_attendees: "",
  description: "",
  agenda_outline: "",
  sponsor_interest: "",
  logo_url: "",
  agree_terms: false,
};

export default function HostEvent() {
  const [form, setForm] = useState(EMPTY_HOST_FORM);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");
  const [uploading, setUploading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadLogo = async (file) => {
    if (!file) return;
    if (file.type !== "image/png")
      return toast.error(
        "Logo must be a PNG (transparent background recommended).",
      );
    if (file.size > 3 * 1024 * 1024)
      return toast.error("Logo must be under 3 MB.");
    setUploading(true);

    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const result = String(reader.result || "");
        set("logo_url", result);
        toast.success("Logo uploaded.");
      } catch {
        toast.error("Logo upload failed.");
      } finally {
        setUploading(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.agree_terms) {
      setErr("Please agree to the privacy & proper-use terms.");
      return;
    }
    setSent(true);
    toast.success("We've received your event brief.");
  };

  return (
    <div data-testid="host-page" className="hostPage">
      <section
        className="hostHero"
        style={{
          background: "linear-gradient(180deg, #061412 0%, #0A1D1A 100%)",
        }}
      >
        <div className="container-x hostHeroInner">
          <p className="overline hostOverline">Host an event</p>

          <div className="hostHeroGrid">
            <div className="hostHeroLeft">
              <h1 className="font-display hostHeroTitle">
                Set the room.
                <br />
                Set the agenda.
                <br />
                <span className="hostAccent">
                  Together, we'll fill the seats.
                </span>
              </h1>

              <p className="hostHeroIntro">
                Tell us who you are and what conversation you want to host. We
                handle the rest — agenda design, virtual platform, moderator
                matching, and the dossier.
              </p>

              <div className="hostStatsGrid" data-testid="host-stats">
                {[
                  ["240+", "Moderators in network"],
                  ["1,200+", "Tables hosted"],
                  ["92%", "Host rebook rate"],
                ].map(([n, l]) => (
                  <div key={l} className="hostStatItem">
                    <p className="font-display hostStatNum">{n}</p>
                    <p className="overline hostStatLabel">{l}</p>
                  </div>
                ))}
              </div>

              <figure className="hostQuote" data-testid="host-quote">
                <blockquote className="font-display hostQuoteText">
                  &ldquo;vmeetio fills my tables with the right six people every
                  time. It's the only host platform I trust with a curated
                  audience.&rdquo;
                </blockquote>
                <figcaption className="overline hostQuoteCaption">
                  Priya M. — Founder, Verdant · 8 events hosted
                </figcaption>
              </figure>
            </div>

            <aside className="hostHeroAside" data-testid="host-hero-aside">
              <div className="glass hostStepsCard">
                <p className="overline hostStepsOverline">
                  Your event in four steps
                </p>
                <ol className="hostStepsList">
                  {[
                    [
                      "01",
                      "Send the brief",
                      "A few sentences on your topic, audience and moderator pick. Two-minute form.",
                    ],
                    [
                      "02",
                      "Co-design the agenda",
                      "We work with you to shape master sessions and choose 6–8 table topics.",
                    ],
                    [
                      "03",
                      "Curate the seats",
                      "Your vmeetio producer fills the room with members who match the conversation.",
                    ],
                    [
                      "04",
                      "Host the event",
                      "You moderate the room. We handle platform, A/V, and the closing dossier.",
                    ],
                  ].map(([n, t, d]) => (
                    <li key={n} className="hostStepItem">
                      <span className="font-display hostStepNum">{n}</span>
                      <div>
                        <p className="font-display hostStepTitle">{t}</p>
                        <p className="hostStepDesc">{d}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <section className="section hostBriefSection">
        <div className="container-x hostBriefGrid">
          <div className="hostBriefLeft">
            <p className="overline hostBriefOverline">What we handle</p>
            <ul className="hostHandleList">
              {[
                "Master-session agenda set in advance",
                "Curated table assignments by topic",
                "Virtual platform — web + app, no downloads",
                "Moderator matching from our network of 240+",
                "Closing dossier with notes and soft intros",
              ].map((x) => (
                <li key={x} className="hostHandleItem">
                  <CheckCircle2
                    size={16}
                    className="hostIconGold hostHandleIcon"
                  />
                  <span>{x}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="hostBriefRight">
            {sent ? (
              <div className="glass hostSuccess" data-testid="host-success">
                <CheckCircle2 className="hostSuccessIcon" size={32} />
                <h3 className="font-display hostSuccessTitle">
                  Brief received
                </h3>
                <p className="hostSuccessBody">
                  A member of the vmeetio team will reach out within two
                  business days.
                </p>
              </div>
            ) : (
              <form
                onSubmit={submit}
                className="glass hostForm"
                data-testid="host-form"
              >
                <p className="overline">Host application</p>
                <h2 className="font-display hostFormTitle">
                  Tell us about you.
                </h2>

                <div className="hostFormGrid2">
                  <label className="hostField">
                    <span className="overline hostLabel">Your full name</span>
                    <input
                      required
                      value={form.name}
                      onChange={(e) => set("name", e.target.value)}
                      data-testid="host-name"
                      className="hostInput"
                    />
                  </label>

                  <label className="hostField">
                    <span className="overline hostLabel">Role</span>
                    <input
                      value={form.role}
                      onChange={(e) => set("role", e.target.value)}
                      data-testid="host-role"
                      placeholder="Founder · Head of Comms · ..."
                      className="hostInput"
                    />
                  </label>

                  <label className="hostField">
                    <span className="overline hostLabel">Email</span>
                    <input
                      required
                      type="email"
                      value={form.email}
                      onChange={(e) => set("email", e.target.value)}
                      data-testid="host-email"
                      className="hostInput"
                    />
                  </label>

                  <label className="hostField">
                    <span className="overline hostLabel">Phone (optional)</span>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={(e) => set("phone", e.target.value)}
                      data-testid="host-phone"
                      className="hostInput"
                    />
                  </label>

                  <label className="hostField">
                    <span className="overline hostLabel">Company</span>
                    <input
                      value={form.company}
                      onChange={(e) => set("company", e.target.value)}
                      data-testid="host-company"
                      className="hostInput"
                    />
                  </label>

                  <label className="hostField">
                    <span className="overline hostLabel">Website</span>
                    <input
                      type="url"
                      value={form.website}
                      onChange={(e) => set("website", e.target.value)}
                      data-testid="host-website"
                      placeholder="https://"
                      className="hostInput"
                    />
                  </label>
                </div>

                <div className="hostFormGrid2">
                  <label className="hostField hostSpan2">
                    <span className="overline hostLabel">
                      Proposed event title
                    </span>
                    <input
                      value={form.proposed_title}
                      onChange={(e) => set("proposed_title", e.target.value)}
                      data-testid="host-proposed-title"
                      placeholder="Raising in a frozen market · Spring edition"
                      className="hostInput"
                    />
                  </label>

                  <label className="hostField">
                    <span className="overline hostLabel">Proposed date</span>
                    <input
                      value={form.proposed_date}
                      onChange={(e) => set("proposed_date", e.target.value)}
                      data-testid="host-proposed-date"
                      placeholder="2026-06-12 or 'late June'"
                      className="hostInput"
                    />
                  </label>

                  <label className="hostField">
                    <span className="overline hostLabel">
                      Expected attendees
                    </span>
                    <input
                      type="number"
                      min="0"
                      value={form.expected_attendees}
                      onChange={(e) =>
                        set("expected_attendees", e.target.value)
                      }
                      data-testid="host-expected-attendees"
                      placeholder="60"
                      className="hostInput"
                    />
                  </label>
                </div>

                <label className="hostField">
                  <span className="overline hostLabel">
                    What's the conversation?
                  </span>
                  <textarea
                    required
                    rows={4}
                    value={form.description}
                    onChange={(e) => set("description", e.target.value)}
                    data-testid="host-description"
                    placeholder="A few sentences on the topic, the audience, and the moderator you have in mind."
                    className="hostTextarea"
                  />
                </label>

                <label className="hostField">
                  <span className="overline hostLabel">
                    Agenda outline (optional)
                  </span>
                  <textarea
                    rows={3}
                    value={form.agenda_outline}
                    onChange={(e) => set("agenda_outline", e.target.value)}
                    data-testid="host-agenda-outline"
                    placeholder="Master session 1 — opening keynote. Master session 2 — table breakouts. ..."
                    className="hostTextarea"
                  />
                </label>

                <label className="hostField">
                  <span className="overline hostLabel">
                    Sponsor interest (optional)
                  </span>
                  <input
                    value={form.sponsor_interest}
                    onChange={(e) => set("sponsor_interest", e.target.value)}
                    data-testid="host-sponsor-interest"
                    placeholder="Looking for a fintech main sponsor; open to introductions."
                    className="hostInput"
                  />
                </label>

                <div className="hostField">
                  <span className="overline hostLabel">
                    Company logo (for the event card)
                  </span>

                  <div className="hostLogoRow">
                    {form.logo_url ? (
                      <img
                        src={form.logo_url}
                        alt="logo"
                        className="hostLogoImg"
                      />
                    ) : (
                      <div className="hostLogoPlaceholder">
                        <ImageIcon size={20} />
                      </div>
                    )}

                    <label className="hostUploadBtn">
                      <Upload size={14} />
                      {uploading
                        ? "Uploading…"
                        : form.logo_url
                          ? "Replace logo"
                          : "Upload logo"}
                      <input
                        type="file"
                        accept="image/png"
                        onChange={(e) => uploadLogo(e.target.files?.[0])}
                        className="hostHiddenInput"
                        data-testid="host-logo-upload"
                      />
                    </label>

                    {form.logo_url && (
                      <button
                        type="button"
                        onClick={() => set("logo_url", "")}
                        className="hostRemoveBtn"
                      >
                        Remove
                      </button>
                    )}
                  </div>

                  <p className="hostLogoNote">
                    PNG only (transparent background recommended), up to 3 MB.
                    We may resize to fit the event card.
                  </p>
                </div>

                <label
                  className="hostTermsRow"
                  data-testid="host-terms-wrapper"
                >
                  <input
                    type="checkbox"
                    checked={form.agree_terms}
                    onChange={(e) => set("agree_terms", e.target.checked)}
                    data-testid="host-terms"
                    className="hostCheckbox"
                  />
                  <span>
                    I agree to host responsibly, treat every attendee with
                    respect and good ethics, confirm I'm authorised to upload
                    the logo above, and accept the{" "}
                    <a href="#" className="hostTermsLink">
                      privacy
                    </a>{" "}
                    and{" "}
                    <a href="#" className="hostTermsLink">
                      proper-use
                    </a>{" "}
                    terms.
                  </span>
                </label>

                {err && <p className="hostError">{err}</p>}

                <button
                  data-testid="host-submit"
                  type="submit"
                  disabled={!form.agree_terms}
                  className="btn-gold hostSubmitBtn"
                >
                  Send brief <ArrowRight size={16} />
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <section className="section hostPerksSection" data-testid="host-perks">
        <div className="container-x">
          <p className="overline hostPerksOverline">Once approved</p>

          <h2 className="font-display hostPerksTitle">
            A private dashboard, magic-link access, and the tools to fill the
            room.
          </h2>

          <div className="hostPerksGrid">
            {[
              {
                title: "90-day magic-link access",
                body: "No passwords. Click the link in your approval email and you're inside your host dashboard. Share with co-hosts if needed.",
              },
              {
                title: "One-click sponsor + attendee invites",
                body: "Paste a list of emails, write your note once, send. Each recipient gets a deep-link straight to the cart (sponsor) or registration page (attendee). Tracked end-to-end.",
              },
              {
                title: "Live KPIs that matter",
                body: "Registrations · tables filling up · sponsorship revenue · invite open rate · invite-to-conversion. See the room build in real time.",
              },
            ].map((p) => (
              <div
                key={p.title}
                className="hostPerkCard"
                data-testid={`host-perk-${p.title
                  .slice(0, 12)
                  .toLowerCase()
                  .replace(/\s+/g, "-")}`}
              >
                <p className="font-display hostPerkTitle">{p.title}</p>
                <p className="hostPerkBody">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section hostFaqSection" data-testid="host-faq">
        <div className="container-x hostFaqContainer">
          <p className="overline hostFaqOverline">Host FAQ</p>

          <div className="hostFaqList">
            {[
              {
                q: "Is hosting free?",
                a: "Yes. vmeetio takes no fee from hosts. We monetise through optional sponsorships your event attracts — and only when you opt in to making sponsorships available.",
              },
              {
                q: "How does the magic-link work?",
                a: "After approval, your link arrives by email and is valid for 90 days. Bookmark it. Anyone with the link can manage your event, so treat it like a password.",
              },
              {
                q: "Can I bring my own moderator?",
                a: "Yes. Pick from our network of 240+ moderators, or nominate your own during the brief. Sponsors can also bring their own moderator at no extra cost.",
              },
              {
                q: "What happens after the event?",
                a: "We send a closing dossier — moderator notes, the consenting attendee list, soft-intro suggestions, sponsor recaps. Use it to follow up with your room.",
              },
              {
                q: "When do invites open?",
                a: "The moment you're approved. Your dashboard gives you a paste-in-emails interface to invite both sponsors and attendees — with personalised CTAs and tracking pixels.",
              },
            ].map((f, i) => (
              <details
                key={i}
                className="hostFaqItem"
                data-testid={`host-faq-${i}`}
              >
                <summary className="hostFaqSummary">
                  <span className="hostFaqQuestion">{f.q}</span>
                  <span className="hostFaqPlus">+</span>
                </summary>
                <p className="hostFaqAnswer">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
